/**
 * @module utils/dateUtils
 * @description Utilidades de formateo de fechas y tamaños de archivo para la UI.
 *
 * Todas las fechas se formatean en español (`es-ES`) con `Intl.DateTimeFormat`.
 * Funciones disponibles:
 * - `formatRelativeTime()`: tiempo relativo ("hace 5 minutos", "ayer a las 14:30")
 * - `formatFileSize()`: bytes a texto legible ("1.4 MB", "230 KB")
 * - `formatFullDateTime()`: fecha y hora completa ("18/05/2026, 14:30")
 */

/**
 * Formatea una fecha a tiempo relativo en español.
 * Ejemplos: "hace 5 minutos", "hace 2 horas", "ayer a las 14:30", "hace 3 días"
 */
export function formatRelativeTime(timestamp: string | Date): string {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  // Menos de 1 minuto
  if (diffInMinutes < 1) {
    return 'hace unos segundos';
  }

  // Menos de 1 hora
  if (diffInMinutes < 60) {
    return `hace ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
  }

  // Menos de 24 horas
  if (diffInHours < 24) {
    return `hace ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
  }

  // Ayer
  if (diffInDays === 1) {
    const timeStr = date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
    return `ayer a las ${timeStr}`;
  }

  // Menos de 7 días
  if (diffInDays < 7) {
    return `hace ${diffInDays} días`;
  }

  // Menos de 30 días
  if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `hace ${weeks} ${weeks === 1 ? 'semana' : 'semanas'}`;
  }

  // Más de 30 días, mostrar fecha
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
}

/**
 * Formatea tamaño de archivo a formato legible
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Formatea un timestamp a fecha y hora completa
 */
export function formatFullDateTime(timestamp: string | Date): string {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;

  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
